// =============================================================================
// ui/shell/ProUpgrade.tsx — the paywall panel, in place of a locked tab.
// -----------------------------------------------------------------------------
// Relocated from the old client's `renderProUpgradePanel` (app/index.html:40149).
// The heading, the body sentence and the button label are VERBATIM — this is the
// product's sales pitch, not copy to improve during a parity port.
//
// ── ⚠ IT REPLACES THE CONTENT, IT DOES NOT SIT ABOVE IT ──
//
// The old client assigns `section.innerHTML`, so a locked tab shows the panel
// INSTEAD OF its screen. Rendering both would leak the whole feature under its
// own paywall.
//
// ── ⚠ WHAT THE BUTTON REACHES TODAY ──
//
// 'account' — the same destination as the old client's CTA. ⚠ My Account's
// plan/upgrade card is NOT ported yet (see `MyAccount.tsx`), so today the button
// lands on a screen that says so. That is a real seam and it is tracked; it is
// not a reason to point the button somewhere the old client does not, or to
// leave the panel without a next step.
//
// ── ⚠ INLINE STYLES, ON PURPOSE ──
//
// `.card`, `.muted` and `.btn primary` are the house classes and already have
// rules; the three layout values are inline exactly as they are in the old
// client. A new class here would have to be added to a stylesheet for
// `scripts/check-styles.mjs` to pass, and it would carry no information the
// three declarations do not.
// =============================================================================

import { sectionFor, TABS } from './tabs';

/**
 * What to call the locked feature.
 *
 * ⚠ The analytics SECTION is named once, not five times — a reader who clicked
 * "Excursion" and is told "Analytics is a Pro feature" is being told the truth
 * about what unlocks, which is the whole section.
 */
export function gatedLabel(tabId: string): string {
  const section = sectionFor(tabId);
  if (section === 'analytics') return 'Analytics';
  return TABS.find((t) => sectionFor(t.id) === section)?.label ?? 'This feature';
}

/**
 * What a gated tab shows while the subscription read is still in flight.
 *
 * 🚨 Not a nicety. Access fails OPEN on an unknown read, so without this a
 * locked screen renders its real content for the frame before the answer lands.
 *
 * ⚠ Never seen while `PAID_LAUNCH` is false — see `useEntitlement`.
 */
export function ProGateChecking() {
  return (
    <div
      className="card"
      data-testid="pro-checking"
      style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center' }}
    >
      <p className="muted">Checking your subscription…</p>
    </div>
  );
}

export interface ProUpgradeProps {
  /** The tab the reader asked for, not the section. */
  tabId: string;
  /** Send them to the plan. */
  go: (tab: string) => void;
}

export function ProUpgrade({ tabId, go }: ProUpgradeProps) {
  const label = gatedLabel(tabId);
  return (
    <div
      className="card"
      data-testid="pro-upgrade"
      data-tab={tabId}
      style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center' }}
    >
      <div style={{ fontSize: 34, lineHeight: 1 }} aria-hidden="true">🔒</div>
      <h2 style={{ margin: '10px 0 4px' }}>{label} is a Pro feature</h2>
      <p className="muted" style={{ fontSize: 14, lineHeight: 1.55 }}>
        Unlock {label} — plus every analytics view, the full Coach&apos;s Report
        (Fix &amp; Prove), AI Coach, backtesting, and unlimited accounts — with
        TradeCoach Pro.
      </p>
      <button
        type="button"
        className="btn primary"
        style={{ marginTop: 14 }}
        onClick={() => go('account')}
      >
        See plans
      </button>
    </div>
  );
}
